<template>
    <div class="home-layout" :class="{ 'is-embedded': embedded }">
      <div class="home-content">
        <!-- 项目列表 -->
        <div class="project-list-container">
          <div class="project-list-header">
            <div class="header-title">
              <h3>项目列表</h3>
              <span class="project-count">({{ filteredProjectList.length }}/{{ projectList.length }})</span>
              <span v-if="projectSortBy" class="sort-indicator">
                <el-tag size="small" type="primary">
                  {{ getSortLabel(projectSortBy) }}
                </el-tag>
              </span>
            </div>
            <div class="header-actions">
              <el-select clearable
                v-model="projectSortBy"
                style="width: 160px; margin-right: 8px;"
                placeholder="排序"
              >
                <el-option label="创建时间(新→旧)" value="createTime" />
                <el-option label="创建时间(旧→新)" value="createTimeAsc" />
                <el-option label="更新时间(新→旧)" value="updateTime" />
                <el-option label="更新时间(旧→新)" value="updateTimeAsc" />
                <el-option label="名称(A→Z)" value="name" />
                <el-option label="名称(Z→A)" value="nameDesc" />
              </el-select>
              <el-button
                v-if="projectSortBy"
                size="small"
                text
                type="primary"
                @click="clearSort"
                style="margin-right: 8px;"
              >
                清除排序
              </el-button>
              <el-input
                v-model="projectSearchText"
                placeholder="搜索项目"
                style="width: 320px; margin-right: 8px;"
                clearable
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </div>
          </div>
          <div class="project-list">
            <div
              v-for="project in filteredProjectList"
              :key="project.id"
              class="project-card"
              @mouseenter="handleProjectHover(project.id)"
              @mouseleave="handleProjectLeave(project.id)"
              @click="handleProjectClick(project)"
            >
              <div class="project-info">
                <div class="project-title-area">
                  <div class="project-name" :title="project.projectName">
                    {{ project.projectName || '-' }}
                  </div>
                  <div class="project-date">
                    <span>{{ formatDate(project.createDate) }}</span>
                  </div>
                </div>

                <div class="project-meta-item">
                  <el-icon><Document /></el-icon>
                  <span class="meta-label">问卷标题：</span>
                  <span class="meta-value" :title="project.questionnaireTitle || '未设置'">{{ project.questionnaireTitle || '未设置' }}</span>
                </div>
                <div class="project-meta-item">
                  <el-icon><Files /></el-icon>
                  <span class="meta-label">报告标题：</span>
                  <span class="meta-value" :title="project.reportTitle || '未设置'">{{ project.reportTitle || '未设置' }}</span>
                </div>
                <div class="project-meta-item">
                  <el-icon><List /></el-icon>
                  <span class="meta-label">任务数量：</span>
                  <span class="meta-value">{{ Number(project.taskCount || 0) }} 个</span>
                </div>
                <div class="project-meta-item">
                  <el-icon><VideoCamera /></el-icon>
                  <span class="meta-label">结果数量：</span>
                  <span class="meta-value">{{ Number(project.resultCount || 0) }} 个</span>
                </div>

                <div class="project-overlay" v-show="hoveredProjectId === project.id">
                  <el-button
                      type="primary"
                      size="small"
                      text
                      icon="Edit"
                      @click.stop="handleRenameProject(project)"
                    >
                      重命名
                  </el-button>
                  <el-button
                      type="primary"
                      size="small"
                      text
                      icon="CopyDocument"
                      @click.stop="handleCopyProject(project)"
                    >
                      复制
                  </el-button>
                  <el-button
                      type="danger"
                      size="small"
                      text
                      icon="Delete"
                      @click.stop="handleDeleteProject(project)"
                    >
                      删除
                  </el-button>
                </div>
              </div>
            </div>
            <div class="empty-state" @click="handleNewProject">
              <el-icon :size="48" color="var(--accent-blue)"><Plus /></el-icon>
              <p>创建项目</p>
            </div>
          </div>

        </div>
      </div>

      <!-- 重命名项目对话框 -->
      <el-dialog v-model="renameDialogVisible" title="重命名项目" width="300px" align-center>
        <el-form>
          <el-form-item label="项目名称">
            <el-input v-model="newProjectName" placeholder="请输入新的项目名称"></el-input>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="renameDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmRename">确定</el-button>
        </template>
      </el-dialog>

      <!-- 新建项目对话框 -->
      <el-dialog v-model="newProjectDialogVisible" title="新建项目" width="300px" align-center>
        <el-form>
          <el-form-item label="项目名称">
            <el-input v-model="newProjectNameInput" placeholder="请输入项目名称"></el-input>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="newProjectDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmNewProject">确定</el-button>
        </template>
      </el-dialog>
    </div>
  </template>

  <script setup>
  import { ref, onMounted, computed, watch } from 'vue'
  import { Plus, Search, Document, Files, List, VideoCamera } from '@element-plus/icons-vue'
  import { ElMessage, ElMessageBox } from 'element-plus'
  import { useRouter } from 'vue-router'
  import { getPathWithAgent } from '@/apps/portal/utils/agentUtils'

  //页面基础components start
  import { getProjectList, addProject, deleteProject, renameProject, copyProject } from '@/api/portal/pm'

  defineProps({
    embedded: {
      type: Boolean,
      default: false,
    },
  })

  // 基础变量声明
  const router = useRouter()

  function isLoggedIn() {
    return !!localStorage.getItem('token')
  }

  function redirectToLogin(message = '请先登录后再创建项目') {
    ElMessage.warning(message)
    router.push(getPathWithAgent('/login'))
  }

  onMounted(() => {
    try {
      // 设置页面标题
      document.title = '项目管理'

      if (isLoggedIn()) {
        fetchProjectList()
      }
    } catch (error) {
      console.error('组件初始化失败:', error)
      ElMessage.error('组件初始化失败，请刷新页面重试')
    }
  })

  //页面基础components end

  const projectList = ref([])

  // 项目相关变量声明
  const hoveredProjectId = ref(null)
  const renameDialogVisible = ref(false)
  const currentRenameProject = ref(null)
  const newProjectName = ref('')
  const newProjectDialogVisible = ref(false)
  const newProjectNameInput = ref('')
  const projectSearchText = ref('')
  const projectSortBy = ref('createTime')


  // 监听排序变化
  watch(projectSortBy, (newSortBy, oldSortBy) => {
    if (newSortBy && newSortBy !== oldSortBy) {
      ElMessage.success(`已按${getSortLabel(newSortBy)}排序`)
    }
  })

  // 过滤和排序后的项目列表
  const filteredProjectList = computed(() => {
    let result = projectList.value

    // 搜索过滤
    if (projectSearchText.value.trim()) {
      result = result.filter(project =>
        project.projectName.toLowerCase().includes(projectSearchText.value.toLowerCase())
      )
    }

    // 排序
    if (projectSortBy.value) {
      result = [...result].sort((a, b) => {
        switch (projectSortBy.value) {
          case 'createTime':
            return new Date(b.createDate) - new Date(a.createDate)
          case 'updateTime':
            return new Date(b.updateDate) - new Date(a.updateDate)
          case 'name':
            return a.projectName.localeCompare(b.projectName, 'zh-CN')
          case 'nameDesc':
            return b.projectName.localeCompare(a.projectName, 'zh-CN')
          case 'createTimeAsc':
            return new Date(a.createDate) - new Date(b.createDate)
          case 'updateTimeAsc':
            return new Date(a.updateDate) - new Date(b.updateDate)
          default:
            return 0
        }
      })
    }

    return result
  })




  // 项目列表相关函数
  function handleProjectHover(projectId) {
    hoveredProjectId.value = projectId
  }

  function handleProjectLeave(projectId) {
    hoveredProjectId.value = null
  }

  // 添加防重复调用的标志
  let isProjectLoading = false

  function handleProjectClick(project) {
    if (!project || !project.id) {
      ElMessage.error('项目信息不完整')
      return
    }

    // 防止重复调用
    if (isProjectLoading) {
      return
    }

    isProjectLoading = true

    try {
      const routeData = router.resolve({
        path: '/project/detail',
        query: {
          projectId: String(project.id)
        }
      })
      window.open(getPathWithAgent(routeData.href), '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('打开项目失败:', error)
      ElMessage.error('打开项目失败，请重试')
    } finally {
      // 加载完成后重置标志
      setTimeout(() => {
        isProjectLoading = false
      }, 100)
    }
  }



  function handleRenameProject(project) {
    currentRenameProject.value = project
    newProjectName.value = project.projectName
    renameDialogVisible.value = true
  }

  async function handleCopyProject(project) {
    try {
      const res = await copyProject(project.id)
      if (res && res.code === 0) {
        ElMessage.success('项目复制成功')
        // 重新获取项目列表
        await fetchProjectList()
      } else {
        ElMessage.error(res.msg || '复制失败')
      }
    } catch (error) {
      console.error('复制项目失败:', error)
      ElMessage.error('复制失败')
    }
  }

  async function handleDeleteProject(project) {
    try {
      await ElMessageBox.confirm(
        `确定要删除项目"${project.projectName}"吗？`,
        '确认删除',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )

      const res = await deleteProject(project.id)
      if (res && res.code === 0) {
        ElMessage.success('删除成功')
        // 重新获取项目列表
        await fetchProjectList()
      } else {
        ElMessage.error(res.msg || '删除失败')
      }
    } catch (error) {
      if (error !== 'cancel') {
        console.error('删除项目失败:', error)
        ElMessage.error('删除失败')
      }
    }
  }

  async function confirmRename() {
    if (!newProjectName.value.trim()) {
      ElMessage.warning('项目名称不能为空')
      return
    }

    if (currentRenameProject.value) {
      try {
        const res = await renameProject(currentRenameProject.value.id, newProjectName.value.trim())
        if (res && res.code === 0) {
          ElMessage.success('重命名成功')
          // 重新获取项目列表
          await fetchProjectList()
          renameDialogVisible.value = false
          currentRenameProject.value = null
          newProjectName.value = ''
        } else {
          ElMessage.error(res.msg || '重命名失败')
        }
      } catch (error) {
        console.error('重命名项目失败:', error)
        ElMessage.error('重命名失败')
      }
    }
  }

  function handleNewProject() {
    if (!isLoggedIn()) {
      redirectToLogin()
      return
    }
    newProjectNameInput.value = ''
    newProjectDialogVisible.value = true
  }

  async function confirmNewProject() {
    if (!isLoggedIn()) {
      redirectToLogin()
      return
    }
    if (!newProjectNameInput.value.trim()) {
      ElMessage.warning('项目名称不能为空')
      return
    }

    try {
      const res = await addProject(newProjectNameInput.value.trim())
      if (res && res.code === 0) {
        ElMessage.success('项目创建成功')
        // 重新获取项目列表
        await fetchProjectList()
        newProjectDialogVisible.value = false
        newProjectNameInput.value = ''
      } else {
        ElMessage.error(res.msg || '创建失败')
      }
    } catch (error) {
      console.error('创建项目失败:', error)
      ElMessage.error('创建失败')
    }
  }

  // 获取项目列表
  async function fetchProjectList() {
    try {
      const res = await getProjectList()
      if (res && res.code === 0) {
        // 后端返回的数据在 res.list 中
        const list = res.list || []
        // 确保日期字段格式正确
        projectList.value = list.map(project => ({
          id: project.id,
          projectName: project.projectName || '',
          createDate: project.createDate ? (typeof project.createDate === 'string' ? project.createDate : new Date(project.createDate).toISOString()) : new Date().toISOString(),
          updateDate: project.updateDate ? (typeof project.updateDate === 'string' ? project.updateDate : new Date(project.updateDate).toISOString()) : new Date().toISOString(),
          questionnaireTitle: project.questionnaireTitle || '',
          reportTitle: project.reportTitle || '',
          taskCount: Number(project.taskCount || 0),
          resultCount: Number(project.resultCount || 0)
        }))
      } else {
        ElMessage.error(res.msg || '获取项目列表失败')
        projectList.value = []
      }
    } catch (error) {
      console.error('获取项目列表失败:', error)
      ElMessage.error('获取项目列表失败，请重试')
      projectList.value = []
    }
  }




  // 格式化日期
  function formatDate(dateString) {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    } catch (error) {
      return dateString
    }
  }

  // 清除排序
  function clearSort() {
    projectSortBy.value = ''
  }

  // 获取排序标签
  function getSortLabel(sortBy) {
    const sortLabels = {
      'createTime': '按创建时间(新→旧)',
      'createTimeAsc': '按创建时间(旧→新)',
      'updateTime': '按更新时间(新→旧)',
      'updateTimeAsc': '按更新时间(旧→新)',
      'name': '按名称(A→Z)',
      'nameDesc': '按名称(Z→A)'
    }
    return sortLabels[sortBy] || '未知排序'
  }


  </script>

<style scoped lang="scss">
.home-layout {
  height: 100%;
  width: 100%;
  background: linear-gradient(180deg, var(--bg-app) 0%, #ffffff 72%);
}

.home-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
}

.home-layout.is-embedded {
  min-height: 0;
  height: 100%;
  background: transparent;
}

.home-layout.is-embedded .home-content,
.home-layout.is-embedded .project-list-container {
  min-height: 0;
  height: 100%;
}

.home-layout.is-embedded .project-list-container {
  overflow-y: auto;
  box-shadow: none;
}

/* 项目列表容器 */
.project-list-container {
  width: 100%;
  background: var(--bg-panel);
  border-radius: var(--radius-lg);
  margin: 0;
  padding: 30px 40px;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  border: 1px solid var(--border-light);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-float);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(107, 114, 128, 0.25);
    border-radius: 3px;
  }
}

/* 列表头部 */
.project-list-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.78);
  border-radius: var(--radius-md);
  border: 1px solid rgba(229, 231, 235, 0.9);
  backdrop-filter: blur(10px);
  position: relative;
}

.project-list-header::before {
  /* 去掉标题左侧竖线 */
  display: none;
}

.header-actions {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.header-title {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
}

.header-title h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
}

.project-count {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: normal;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(79, 70, 229, 0.15);
  background: rgba(79, 70, 229, 0.06);
}

.sort-indicator {
  margin-left: 4px;
}

/* 项目卡片网格 */
.project-list {
  display: flex;
  flex-wrap: wrap;
  padding: 12px 0 16px;
  gap: 14px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(229, 231, 235, 0.9) transparent;
}

/* 项目卡片 */
.project-card {
  /* 其余区域只做很浅的紫蓝色底色，避免“全卡渐变” */
  background: linear-gradient(
    180deg,
    rgba(79, 70, 229, 0.03) 0%,
    rgba(147, 51, 234, 0.015) 35%,
    rgba(255, 255, 255, 0.85) 100%
  );
  width: 240px;
  min-height: 190px;
  border-radius: 16px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
  border: 1px solid rgba(229, 231, 235, 0.95);
  position: relative;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background:
      radial-gradient(260px 90px at 50% 0%, rgba(79, 70, 229, 0.12), transparent 70%);
    opacity: 0.25;
    z-index: 0;
    transition: opacity 0.25s ease, transform 0.25s ease;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    border-color: var(--accent-blue, #4F46E5);
    transform: translateY(-5px);
    box-shadow: var(--shadow-hover), 0 0 0 1px rgba(79, 70, 229, 0.08);
  }

  &:hover::after {
    /* 标题保持主视觉，其余区域只做轻微呼吸 */
    opacity: 0.35;
    transform: scale(1.02);
  }

  &:active {
    transform: translateY(0);
  }
}

/* 悬浮操作层 */
.project-overlay {
  position: absolute;
  /* 覆盖到底边和左右边贴合卡片边缘（抵消 project-card 的 padding: 16px） */
  left: -16px;
  right: -16px;
  bottom: -36px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.65) 0%, rgba(255, 255, 255, 0.97) 100%);
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease forwards;
  z-index: 10;
  padding: 10px 12px;
  border-top: 1px solid rgba(79, 70, 229, 0.18);
  border-radius: 0 0 var(--radius-md) var(--radius-md);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 项目信息 */
.project-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.project-title-area {
  /* 把渐变底色贴齐卡片左右边缘（抵消 project-card 的 padding:16px） */
  position: relative;
  margin-left: -16px;
  margin-right: -16px;
  margin-top: -16px;
  padding: 6px 16px 0 16px;
  overflow: hidden;
  border-radius: 16px 16px 0px 0px;
}

.project-title-area::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(135deg, rgba(79, 70, 229, 0.10) 0%, rgba(147, 51, 234, 0.08) 55%, rgba(99, 102, 241, 0.05) 100%);
  opacity: 1;
  pointer-events: none;
}

.project-title-area > * {
  position: relative;
  z-index: 1;
}

.project-name {
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-date {
  color: var(--text-secondary);
  font-size: 11px;
  font-family: 'Consolas', monospace;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  margin-top: 2px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.95);
  opacity: 0.8;
}

.project-meta-item {
  display: flex;
  font-size: 12px;
  line-height: 1.6;
  color: var(--text-secondary);
  gap: 4px;
  align-items: center;
}

.meta-label {
  color: var(--text-secondary);
  opacity: 0.8;
  flex-shrink: 0;
}

.meta-value {
  color: var(--text-primary);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

/* 空状态/新建卡片 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--bg-card);
  color: var(--text-secondary);
  width: 240px;
  min-height: 190px;
  border-radius: 16px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px dashed rgba(209, 213, 219, 0.95);
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    inset: -1px;
    background:
      radial-gradient(520px 180px at 50% 0%, rgba(79, 70, 229, 0.18), transparent 62%),
      radial-gradient(420px 180px at 85% 35%, rgba(147, 51, 234, 0.13), transparent 60%);
    opacity: 0.4;
    transition: opacity 0.25s ease;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    border-color: var(--accent-blue, #4F46E5);
    color: var(--accent-blue, #4F46E5);
    transform: translateY(-5px);
    box-shadow: 0 16px 40px rgba(79, 70, 229, 0.12);

    &::after {
      opacity: 0.95;
    }
  }
}

.empty-state p {
  margin: 12px 0 0;
  font-size: 14px;
  font-weight: 650;
}

.project-list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid #E5E7EB;
  margin-top: 16px;
}

.footer-actions {
  display: flex;
  gap: 8px;
}

.footer-info {
  color: var(--text-secondary);
  font-size: 12px;
}
</style>
